/**
 * Web-only AdSense surface.
 *
 * The native Capacitor app deliberately returns nothing: AdSense is not a
 * permitted in-app WebView integration. Native placements must be supplied by
 * the Google Mobile Ads SDK / AdMob once its separate GrowDaily app is ready.
 */
import { useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const CONSENT_KEY = 'gdpr_consent_v1';

function marketingAllowed() {
    try { return JSON.parse(localStorage.getItem(CONSENT_KEY))?.marketing === true; }
    catch { return false; }
}

function isNative() {
    return !!(window.Capacitor?.isNativePlatform?.());
}

function loadScript(client) {
    if (document.querySelector('script[data-growdaily-adsense]')) return;
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.growdailyAdsense = 'true';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    document.head.appendChild(script);
}

export default function WebAdSlot({ slot, className = '' }) {
    const { user } = useAuth();
    const client = process.env.REACT_APP_ADSENSE_CLIENT || '';
    const allowed = useMemo(() => Boolean(client && slot && !isNative() && !user?.premium && marketingAllowed()), [client, slot, user?.premium]);

    useEffect(() => {
        if (!allowed) return undefined;
        loadScript(client);
        const timer = window.setTimeout(() => {
            try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch { /* ad blockers / no fill */ }
        }, 0);
        return () => window.clearTimeout(timer);
    }, [allowed, client, slot]);

    if (!allowed) return null;
    return (
        <aside className={`gd-web-ad ${className}`} aria-label="Advertisement">
            <span className="gd-web-ad-label">Advertisement</span>
            <ins className="adsbygoogle"
                style={{ display: 'block' }}
                data-ad-client={client}
                data-ad-slot={slot}
                data-ad-format="auto"
                data-full-width-responsive="true" />
        </aside>
    );
}
